// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § API contract (sign-in sequence), § Crew sign-in
// ==============================================
// /r/<token> — where a crew member lands from the request email (Crew-1, Crew-2).
//
//   1. GET the public view (side-effect free: mail scanners prefetch links).
//   2. Signed in already? Claim the request with the token:
//        ok            → the checklist
//        WRONG_ACCOUNT → "sign out and continue as s***@…", then step 3
//   3. "Provide my papers" emails a 6-digit code to the recipient's own address
//      (the page never sends or shows the full address).
//   4. The code signs in (creating a papers-only account for a new address) and
//      claims the request; a full page load then lands on the checklist, so the
//      auth provider reads the new session.
//   5. An account with no name yet (a new one: the API never saves the name the
//      skipper typed) is asked for it first, with the skipper's version offered.
// Any link that does not open a request gets one identical page; 429s say how
// long to wait. Nothing is claimed or sent until the person presses a button,
// except the claim for someone already signed in (it needs their session).
// ==============================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../services/api';
import { useAuth } from '../../../providers/AuthProvider';
import { hardNavigate } from '../navigation';
import { apiError, waitLabel } from '../apiError';
import type { ApiErrorInfo } from '../apiError';
import type { PublicRequestView } from '../types';
import { fmtDay, firstName, passageLabel } from '../dates';
import { C, CrewShell, ErrorNote, PrimaryButton, StepHeading, Loading } from '../ui';

type Phase = 'loading' | 'invalid' | 'landing' | 'claiming' | 'wrong' | 'code' | 'name' | 'fatal';

const withWait = (e: ApiErrorInfo) => (e.status === 429 && e.retryAfter ? `${e.message}. Try again in ${waitLabel(e.retryAfter)}.` : e.message);

export function RequestLinkPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user, logout } = useAuth();
  const [phase, setPhase] = useState<Phase>('loading');
  const [view, setView] = useState<PublicRequestView | null>(null);
  const [masked, setMasked] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [sentAgain, setSentAgain] = useState(false);
  const triedClaim = useRef(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const lastSubmitted = useRef<string | null>(null);
  const [name, setName] = useState('');
  const [nextPath, setNextPath] = useState<string | null>(null);

  const fail = useCallback((e: ApiErrorInfo, where: Phase) => {
    if (e.status === 404) { setPhase('invalid'); return; }
    setError(withWait(e));
    setPhase(where);
  }, []);

  // 1. the public view
  useEffect(() => {
    let cancelled = false;
    api.crewLinkView(token)
      .then((v) => { if (!cancelled) { setView(v); setMasked(v.maskedEmail); } })
      .catch((err) => { if (!cancelled) fail(apiError(err, 'This link could not be opened'), 'fatal'); });
    return () => { cancelled = true; };
  }, [token, fail]);

  // 2. already signed in → claim, or explain the wrong account
  useEffect(() => {
    if (!view || authLoading || phase !== 'loading') return;
    if (!isAuthenticated) { setPhase('landing'); return; }
    if (triedClaim.current) return;
    triedClaim.current = true;
    setPhase('claiming');
    api.crewClaim(token)
      .then((r) => navigate(`/papers/requests/${r.requestId}`, { replace: true }))
      .catch((err) => {
        const e = apiError(err, 'This request could not be opened');
        if (e.code === 'WRONG_ACCOUNT') { if (e.maskedEmail) setMasked(e.maskedEmail); setPhase('wrong'); return; }
        fail(e, 'fatal');
      });
  }, [view, authLoading, isAuthenticated, phase, token, navigate, fail]);

  // 3. email a code
  const sendCode = async (again = false) => {
    setBusy(true); setError(null); setSentAgain(false);
    try {
      const r = await api.crewSendCode(token);
      setMasked(r.maskedEmail);
      setCode(''); lastSubmitted.current = null;
      setPhase('code');
      if (again) setSentAgain(true);
      setTimeout(() => codeRef.current?.focus(), 0);
    } catch (err) {
      fail(apiError(err, 'The code could not be sent'), again ? 'code' : 'landing');
    } finally { setBusy(false); }
  };

  // 4. sign in with it
  const signIn = async (value: string) => {
    if (busy || value.length !== 6 || lastSubmitted.current === value) return;
    lastSubmitted.current = value;
    setBusy(true); setError(null); setSentAgain(false);
    try {
      const r = await api.crewSignIn(token, value);
      const path = `/papers/requests/${r.requestId}`;
      if (!r.user.name?.trim()) {
        setName(r.suggestedName ?? view?.recipientName ?? '');
        setNextPath(path);
        setPhase('name');
        setBusy(false);
        return;
      }
      hardNavigate(path);
    } catch (err) {
      const e = apiError(err, 'That code could not be checked');
      if (e.status === 404) { setPhase('invalid'); return; }
      setError(withWait(e));
      setCode(''); lastSubmitted.current = null;
      setBusy(false);
      setTimeout(() => codeRef.current?.focus(), 0);
    }
  };

  // 5. a new account's own name
  const saveName = async () => {
    const clean = name.trim().replace(/\s+/g, ' ');
    if (!clean || busy || !nextPath) return;
    setBusy(true); setError(null);
    try {
      await api.updateProfile({ name: clean.slice(0, 120) });
      hardNavigate(nextPath);
    } catch {
      setError('Your name could not be saved. Try again.');
      setBusy(false);
    }
  };

  const onCode = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) void signIn(digits); // autofill and paste finish by themselves
  };

  const switchAccount = async () => {
    setBusy(true);
    try { await logout(); } finally { setBusy(false); }
    setError(null);
    setPhase('landing');
  };

  const who = firstName(view?.requesterName);
  const passage = passageLabel(view?.passage.from, view?.passage.to);

  if (phase === 'invalid') {
    return (
      <CrewShell>
        <div className="flex flex-col gap-3 pt-6">
          <h1 className="m-0 text-[26px] font-bold leading-tight">This link has expired or was replaced</h1>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>Links stop working when the skipper sends a new one, or once the passage is over. Please ask the skipper to resend it.</p>
        </div>
      </CrewShell>
    );
  }

  if (phase === 'loading' || phase === 'claiming' || !view) {
    return <CrewShell>{phase === 'fatal' ? <ErrorNote>{error}</ErrorNote> : <Loading label={phase === 'claiming' ? 'Opening your request…' : 'Loading…'} />}</CrewShell>;
  }

  if (phase === 'wrong') {
    return (
      <CrewShell right={user?.email}>
        <div className="flex flex-col gap-3 pt-4">
          <StepHeading focusKey="wrong">This request is for someone else</StepHeading>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>
            {who} sent it to <strong className="text-white">{masked}</strong>, but you’re signed in as <strong className="text-white">{user?.email}</strong>. Sign out, and we’ll email a code to the right address.
          </p>
        </div>
        <PrimaryButton onClick={() => void switchAccount()} busy={busy}>Sign out and continue as {masked}</PrimaryButton>
        <p className={`m-0 text-sm leading-relaxed ${C.muted}`}>If this isn’t your address, ask {who} to send the request to the one you use.</p>
      </CrewShell>
    );
  }

  if (phase === 'name') {
    return (
      <CrewShell right={view.requesterName ? `For ${view.requesterName}` : undefined}>
        <div className="flex flex-col gap-3 pt-6">
          <StepHeading focusKey="name" className="text-[28px]">What’s your name?</StepHeading>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>It goes on your account and on the papers you share. {who} typed the name below — change it if it isn’t how you write it.</p>
        </div>
        <form className="flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); void saveName(); }}>
          <div className="flex flex-col gap-2.5">
            <label htmlFor="crew-name" className="text-sm font-semibold text-[#c7d3e6]">Your name</label>
            <input
              id="crew-name" name="name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={120} autoFocus disabled={busy}
              className="h-[52px] w-full rounded-xl border border-[#2a4a86] bg-[#081a3b] px-4 text-[17px] text-white focus:border-[#5aa9ff] focus:outline-none focus:ring-2 focus:ring-[#5aa9ff]"
            />
          </div>
          <ErrorNote>{error}</ErrorNote>
          <PrimaryButton type="submit" busy={busy} disabled={!name.trim()}>{busy ? 'Saving…' : 'Continue'}</PrimaryButton>
        </form>
      </CrewShell>
    );
  }

  if (phase === 'code') {
    return (
      <CrewShell right={view.requesterName ? `For ${view.requesterName}` : undefined}>
        <div className="flex flex-col gap-3 pt-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0f2856] text-[#9fcbff]">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
          </div>
          <StepHeading focusKey="code" className="text-[28px]">Check your email</StepHeading>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>We sent a 6-digit code to <strong className="text-white">{masked}</strong>. This means a forwarded link is useless to anyone else.</p>
        </div>
        <form className="flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); void signIn(code); }}>
          <div className="flex flex-col gap-2.5">
            <label htmlFor="crew-code" className="text-sm font-semibold text-[#c7d3e6]">Code</label>
            <input
              id="crew-code" ref={codeRef} name="code" value={code} onChange={(e) => onCode(e.target.value)}
              autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*" maxLength={7} autoFocus
              aria-describedby="crew-code-help" aria-invalid={error ? true : undefined} disabled={busy}
              className="h-[58px] w-full rounded-xl border border-[#2a4a86] bg-[#081a3b] px-4 text-center font-mono text-[28px] font-medium tracking-[0.5em] text-white focus:border-[#5aa9ff] focus:outline-none focus:ring-2 focus:ring-[#5aa9ff]"
            />
            <span id="crew-code-help" className={`text-sm ${C.muted}`}>Your phone can fill it in from the email.</span>
          </div>
          <ErrorNote>{error}</ErrorNote>
          {sentAgain && <p role="status" className="m-0 text-[15px] text-[#6ee7a2]">A new code is on its way.</p>}
          <PrimaryButton type="submit" busy={busy} disabled={code.length !== 6}>{busy ? 'Checking…' : 'Continue'}</PrimaryButton>
        </form>
        <div className={`mt-auto flex flex-col gap-2.5 text-sm leading-relaxed ${C.muted}`}>
          <p className="m-0">The code works once and lasts 10 minutes. Next time on this phone the link signs you straight in.</p>
          <button type="button" onClick={() => void sendCode(true)} disabled={busy} className={`min-h-[44px] self-start text-left text-[15px] ${C.link} ${C.focus}`}>Send a new code</button>
        </div>
      </CrewShell>
    );
  }

  // landing (Crew-1: what the email said, on the page)
  const n = view.itemCount;
  return (
    <CrewShell right={view.requesterName ? `For ${view.requesterName}` : undefined}>
      <div className={`${C.card} overflow-hidden`}>
        <div className="flex flex-col gap-1 bg-[#061430] px-5 py-4 border-b border-[#1d3a70]">
          <span className={C.label}>Crew papers</span>
          {passage && <span className="text-lg font-bold text-white">{passage}{view.passage.departOn ? ` · sails ${fmtDay(view.passage.departOn)}` : ''}</span>}
        </div>
        <div className="flex flex-col gap-4 p-5">
          <StepHeading focusKey="landing" className="text-[22px]">{who} needs {n} paper{n === 1 ? '' : 's'} from you{passage ? ` for ${passage}` : ''}</StepHeading>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>
            Hi {firstName(view.recipientName, 'there')}, {who} is getting the crew’s papers together{view.vesselName ? ` for ${view.vesselName}` : ''} before you sail.
            {view.dueOn ? <> They’re needed by <strong className="text-white">{fmtDay(view.dueOn)}</strong>.</> : null}
          </p>
          {view.status === 'complete' && <p className="m-0 text-[15px] text-[#6ee7a2]">You’ve already provided everything. You can still check or change what you shared.</p>}
          <ErrorNote>{error}</ErrorNote>
          <PrimaryButton onClick={() => void sendCode()} busy={busy}>{busy ? 'Sending a code…' : 'Provide my papers'}</PrimaryButton>
          <p className={`m-0 text-sm leading-relaxed ${C.muted}`}>We’ll email a 6-digit code to {masked} to check it’s you. About two minutes on your phone. No app, no password. Only {who} and you will see them, and you choose when {who}’s access ends.</p>
        </div>
      </div>
      <p className={`m-0 text-sm leading-relaxed ${C.muted}`}>Weren’t expecting this? You can close this page. Nothing is shared unless you choose to.</p>
    </CrewShell>
  );
}

export default RequestLinkPage;
